// PlayFirst Parent — header notifications bell.
//
// Key layout decisions (Feb 2026 rewrite):
//  • The panel escapes any ancestor `overflow-hidden` (the PFP Home has one
//    on its outer gradient wrapper) by rendering into `document.body` via
//    `createPortal`.
//  • Uses `position: fixed` so it stays on top of any absolutely-positioned
//    hero content — never hidden behind other writing.
//  • Mobile: full-width bottom-sheet-style panel with backdrop.
//  • Desktop: floating anchored to the top-right of the viewport.
//  • Clicking a notification opens a full-content Dialog so parents can
//    actually read the whole tip — no more truncation.
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Bell, X, Sparkles, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

// Feb 2026 — the bell renders a mix of Top Tip Tuesday tips AND Clinical
// mirror notifications (weekly focus, session note, general feedback from a
// therapist). Previously every row was hard-coded as "Top Tip Tuesday",
// which meant a therapist Weekly Focus arrived under the wrong heading.
// Derive the label + fallback title from the row's `type` (Clinical mirror
// + notify_parent) or `kind` (legacy Top Tip Tuesday writer) so each
// content kind stays clearly separate.
function notificationKind(n) {
  const type = (n && n.type) || '';
  const kind = (n && n.kind) || '';
  // Legacy top-tip writer only sets `kind: 'top_tip_tuesday'` — no `type`.
  const isTopTip = kind === 'top_tip_tuesday' || type === 'top_tip' || (!type && !kind);
  if (isTopTip) {
    return {
      label: 'Top Tip Tuesday',
      fallbackTitle: 'Top Tip',
      stripPrefix: /^Top Tip Tuesday:.+?\n+/,
    };
  }
  if (type === 'clinical_weekly_focus') {
    return {
      label: 'Weekly Focus',
      fallbackTitle: 'Weekly Focus from your therapist',
      stripPrefix: null,
    };
  }
  if (type === 'clinical_progress_note') {
    return {
      label: 'Session Note',
      fallbackTitle: 'Session note from your therapist',
      stripPrefix: null,
    };
  }
  if (type.startsWith('clinical_') || type === 'therapist_link_accepted') {
    return {
      label: 'From your therapist',
      fallbackTitle: 'Update from your therapist',
      stripPrefix: null,
    };
  }
  // Any other structured notification (e.g. pfp_priority_goals_updated) —
  // keep it neutral rather than mislabelling as a Top Tip.
  return {
    label: 'Update',
    fallbackTitle: 'PlayFirst update',
    stripPrefix: null,
  };
}

function relTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reading, setReading] = useState(null); // notification currently opened in the read dialog
  const btnRef = useRef(null);

  const refreshCount = async () => {
    try {
      const r = await axios.get(`${API}/parent-app/notifications/unread-count`, auth());
      setUnread(r.data?.count || 0);
    } catch { /* ignore */ }
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/parent-app/notifications?limit=20`, auth());
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshCount();
    const t = setInterval(refreshCount, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (open) loadItems();
  }, [open]);

  // Lock body scroll while the panel is open on mobile.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open && !reading) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (reading) setReading(null);
        else setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, reading]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/parent-app/notifications/${id}/read`, {}, auth());
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      refreshCount();
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/parent-app/notifications/mark-all-read`, {}, auth());
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* ignore */ }
  };

  const openNotification = (n) => {
    setReading(n);
    if (!n.read) markRead(n.id);
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="p-2 rounded-full hover:bg-[#7C6F9C]/10 relative"
        data-testid="parent-app-notifications-btn"
        title="Notifications"
      >
        <Bell className="w-5 h-5 text-stone-600" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold inline-flex items-center justify-center"
            data-testid="parent-app-notifications-badge"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && createPortal(
        <>
          {/* Backdrop — mobile darkens, desktop soft-blurs. Also closes the panel on tap. */}
          <div
            className="fixed inset-0 z-[9998] bg-black/30 sm:bg-black/10 backdrop-blur-[1px]"
            onClick={() => setOpen(false)}
            data-testid="parent-app-notifications-backdrop"
          />
          {/* Panel — mobile: bottom-sheet full-width. Desktop: floating top-right card. */}
          <div
            className="fixed z-[9999] bg-white shadow-2xl border border-stone-200 flex flex-col overflow-hidden
                       bottom-0 left-0 right-0 rounded-t-3xl max-h-[85vh]
                       sm:bottom-auto sm:left-auto sm:right-4 sm:top-16 sm:w-[400px] sm:max-w-[calc(100vw-2rem)] sm:rounded-2xl sm:max-h-[70vh]"
            data-testid="parent-app-notifications-panel"
          >
            {/* Mobile drag handle */}
            <div className="sm:hidden flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 rounded-full bg-stone-300" />
            </div>

            <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#7C6F9C]" />
                <span className="text-sm font-medium text-[#3a2f4f]">Notifications</span>
              </div>
              <div className="flex items-center gap-2">
                {items.some((n) => !n.read) && (
                  <button
                    onClick={markAllRead}
                    className="text-xs text-[#7C6F9C] hover:underline"
                    data-testid="parent-app-notifications-mark-all-read"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="p-1 rounded-full hover:bg-stone-100"
                  aria-label="Close notifications"
                  data-testid="parent-app-notifications-close"
                >
                  <X className="w-4 h-4 text-stone-500" />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto flex-1">
              {loading ? (
                <div className="px-4 py-6 text-sm text-stone-500">Loading…</div>
              ) : items.length === 0 ? (
                <div className="px-4 py-10 text-center" data-testid="parent-app-notifications-empty">
                  <p className="text-sm text-stone-600 mb-1">No notifications yet 🌱</p>
                  <p className="text-xs text-stone-400">
                    Top Tip Tuesdays land here every Tuesday.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-stone-100">
                {items.map((n) => {
                    const kind = notificationKind(n);
                    const bodyText = kind.stripPrefix
                      ? (n.body || '').replace(kind.stripPrefix, '')
                      : (n.body || '');
                    return (
                    <li
                      key={n.id}
                      className={`px-4 py-3 cursor-pointer hover:bg-stone-50 transition ${
                        n.read ? '' : 'bg-[#FAF7FF]'
                      }`}
                      data-testid={`parent-app-notification-${n.id}`}
                      onClick={() => openNotification(n)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openNotification(n)}
                    >
                      <div className="flex items-start gap-2">
                        {!n.read && (
                          <span className="mt-1.5 w-2 h-2 rounded-full bg-rose-500 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span
                              className="text-[11px] uppercase tracking-wide text-[#7C6F9C] font-medium"
                              data-testid={`parent-app-notification-kind-${n.id}`}
                            >
                              {kind.label}
                            </span>
                            <span className="text-[11px] text-stone-400 flex-shrink-0">
                              {relTime(n.created_at)}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-[#3a2f4f] mt-0.5 truncate">
                            {n.tip_title || n.title || kind.fallbackTitle}
                          </p>
                          <p className="text-xs text-stone-500 mt-0.5 line-clamp-2">
                            {bodyText.slice(0, 140)}
                          </p>
                          <span className="text-[11px] text-[#7C6F9C] font-medium mt-1 inline-block">
                            Tap to read →
                          </span>
                        </div>
                      </div>
                    </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </>,
        document.body,
      )}

      {/* Full-content read dialog — mounted at body level so it's never
          clipped by an ancestor overflow. */}
      {reading && (() => {
        const kind = notificationKind(reading);
        return createPortal(
        <>
          <div
            className="fixed inset-0 z-[10000] bg-black/50 backdrop-blur-sm"
            onClick={() => setReading(null)}
            data-testid="parent-app-notification-read-backdrop"
          />
          <div
            className="fixed z-[10001] bg-white shadow-2xl overflow-hidden flex flex-col
                       bottom-0 left-0 right-0 rounded-t-3xl max-h-[92vh]
                       sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:bottom-auto sm:right-auto sm:w-[92vw] sm:max-w-[560px] sm:rounded-3xl sm:max-h-[85vh]"
            data-testid="parent-app-notification-read-dialog"
            role="dialog"
            aria-modal="true"
          >
            <div className="sm:hidden flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 rounded-full bg-stone-300" />
            </div>
            <div className="px-5 py-4 border-b border-stone-100 flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#7C6F9C]" />
                  <span
                    className="text-[11px] uppercase tracking-wider text-[#7C6F9C] font-semibold"
                    data-testid="parent-app-notification-read-kind"
                  >
                    {kind.label}
                  </span>
                  <span className="text-[11px] text-stone-400">· {relTime(reading.created_at)}</span>
                </div>
                <h2 className="font-serif text-xl text-[#3a2f4f] mt-1 leading-snug">
                  {reading.tip_title || reading.title || kind.fallbackTitle}
                </h2>
              </div>
              <button
                onClick={() => setReading(null)}
                className="p-2 rounded-full hover:bg-stone-100 flex-shrink-0"
                aria-label="Close"
                data-testid="parent-app-notification-read-close"
              >
                <X className="w-5 h-5 text-stone-500" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 px-5 py-4">
              <pre className="text-sm text-stone-700 whitespace-pre-wrap font-sans leading-relaxed" data-testid="parent-app-notification-read-body">
                {reading.body}
              </pre>
            </div>

            <div className="px-5 py-3 border-t border-stone-100 flex items-center justify-between gap-2">
              <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> Marked as read
              </span>
              <div className="flex items-center gap-2">
                {reading.cta_path && (
                  <a
                    href={reading.cta_path}
                    className="text-sm font-medium text-white bg-[#7C6F9C] hover:bg-[#6b5f8a] rounded-full px-3 py-1"
                    data-testid="parent-app-notification-read-open"
                  >
                    Open
                  </a>
                )}
                <button
                  onClick={() => setReading(null)}
                  className="text-sm font-medium text-[#7C6F9C] hover:underline px-2 py-1"
                  data-testid="parent-app-notification-read-done"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </>,
        document.body,
        );
      })()}
    </>
  );
}
